import React, { useEffect, useMemo, useState } from 'react';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { ContentParagraph } from '../../../../_lib/components/ContentParagraph';
import { SearchBreadcrumb } from '../../../../components/molecules/SearchBreadcrumb';
import { ContentLink } from '../../../../_lib/components/ContentLink';
import {
  ListBoxModifier,
  ContentSelectComboBox,
  TopContentResult,
} from '../../../../components/molecules/Form/components/ComboBox';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { ComboBoxParam, Row, SubmitButton } from '../../../../_lib/Form2';
import { FetchState } from '../../../../hooks/reducers/useFetchReducer';
import { SonResponseError } from '../../../../_lib/Form2/types/SonResponseError';
import { LoadingSpinnerContainer } from '../../../../components/atoms/LoadingSpinner';
import LiveRegion from '../../../../components/atoms/LiveRegion/LiveRegion';
import { Hint } from '../../../../_lib/components/Hint';
import { useModalContext } from '../../../../components/organisms/ModalDialog/ModalContext';
import { RowModifier } from '../../../../_lib/Form2/grid/Row/Row';

export type PageMoveFormTemplateProps = {
  hasExplicitReadRestrictions?: boolean;
  hasChildPages?: boolean;
  readRestrictionsComparisonUrl?: {
    url: string;
  };
  fetchState: FetchState<AxiosResponse<any>, AxiosError<SonResponseError>>;
  newParentPageIdParam: ComboBoxParam<'newParentPageId'>;
  resetFetchState: () => void;
};

export function PageMoveFormTemplate({
  hasExplicitReadRestrictions,
  hasChildPages,
  readRestrictionsComparisonUrl,
  fetchState,
  newParentPageIdParam,
  resetFetchState,
}: PageMoveFormTemplateProps) {
  const { t } = useSonTranslation('PageManagement');
  const [selectedPage, setSelectedPage] = useState<TopContentResult | undefined>();
  const [readRestrictionsWarning, setReadRestrictionsWarning] = useState(false);
  const [checkingReadRestrictions, setCheckingReadRestrictions] = useState(false);

  const { preventModalClose } = useModalContext();

  useEffect(() => {
    preventModalClose(fetchState.type === 'loading');
  }, [fetchState, preventModalClose]);

  const pageMoveFormCustomErrorTypeMessages = useMemo(
    () => ({
      pageMoveInvalidPageTypeOfMovedPage: t(
        'views.navigationsettings-move.form.status.pageMoveInvalidPageTypeOfMovedPage'
      ),
      pageMoveInvalidPageTypeOfNewParent: t(
        'views.navigationsettings-move.form.status.pageMoveInvalidPageTypeOfNewParent'
      ),
      pageMoveInvalidPageForNewParent: t('views.navigationsettings-move.form.status.pageMoveInvalidPageForNewParent'),
      pageMoveMissingPrivilegesForNewParentPage: t(
        'views.navigationsettings-move.form.status.pageMoveMissingPrivilegesForNewParentPage'
      ),
      pageMoveCanNotMoveToOwnChildPages: t(
        'views.navigationsettings-move.form.status.pageMoveCanNotMoveToOwnChildPages'
      ),
      pageMoveInvalidVersionStateOfNewParent: t(
        'views.navigationsettings-move.form.status.pageMoveInvalidVersionStateOfNewParent'
      ),
    }),
    [t]
  );

  const setReadRestrictionWarningAsync = async (selectedPageId: string, comparisonUrl: string) => {
    if (selectedPageId) {
      try {
        const response = await axios.get(`${comparisonUrl}&targetPageId=${selectedPageId}`);
        if (response.data) {
          setReadRestrictionsWarning(response.data.showWarning);
        }
      } catch {
        setReadRestrictionsWarning(false);
      } finally {
        setCheckingReadRestrictions(false);
      }
    }
  };

  return (
    <>
      {hasChildPages || hasExplicitReadRestrictions ? (
        <Row modifier={RowModifier.DISPLAY_FLEX_DIRECTION_COLUMN}>
          {hasChildPages ? <Hint message={t('views.navigationsettings-move.form.hint')} /> : null}
          {hasExplicitReadRestrictions ? (
            <ContentParagraph>{t('views.navigationsettings-move.form.hintReadRestrictions')}</ContentParagraph>
          ) : null}
        </Row>
      ) : null}
      <Row>
        <ContentSelectComboBox
          id="combobox-pagemove"
          name={undefined}
          hideLabelAddition
          modifierListBox={ListBoxModifier.RELATIVE} // listbox expands modal dialog
          suggestUrl={newParentPageIdParam?.comboBoxUrl?.url || ''}
          ariaAutoComplete="list"
          labelTitle={t('views.navigationsettings-move.form.newParentPage.label')}
          labelHelpText={t('views.navigationsettings-move.form.newParentPage.labelHelpText')}
          onSelection={item => {
            setSelectedPage(item ? ({ ...item } as TopContentResult) : undefined);
            setReadRestrictionsWarning(false);
            resetFetchState();

            if (readRestrictionsComparisonUrl && item && !hasExplicitReadRestrictions) {
              setCheckingReadRestrictions(true);
              setReadRestrictionWarningAsync(item.id, readRestrictionsComparisonUrl.url);
            }
          }}
        />
      </Row>
      {selectedPage && (
        <Row modifier={RowModifier.BLOCK}>
          <input type="hidden" name={newParentPageIdParam.name} value={selectedPage?.id} />
          <h3 className="form__headline">{t('views.navigationsettings-move.form.selectedPage')}</h3>
          <ContentLink to={selectedPage.pageLink.url}>{selectedPage.title}</ContentLink>
          {selectedPage.breadcrumb && <SearchBreadcrumb clickable breadcrumbs={selectedPage.breadcrumb} />}
        </Row>
      )}
      {!readRestrictionsWarning && checkingReadRestrictions ? (
        <Row>
          <LoadingSpinnerContainer />
          <LiveRegion invisible={true}>
            {t('views.navigationsettings-move.form.loadingReadRestrictionsCheck')}
          </LiveRegion>
        </Row>
      ) : null}
      {readRestrictionsWarning && !checkingReadRestrictions ? (
        <Row>
          <ContentParagraph>{t('views.navigationsettings-move.form.hintReadRestrictionsComparison')}</ContentParagraph>
        </Row>
      ) : null}
      <SubmitButton
        buttonLabel={t('views.navigationsettings-move.form.saveButtonLabel')}
        messageSuccess={t('views.navigationsettings-move.form.status.success')}
        messageSending={t('views.navigationsettings-move.form.status.sending')}
        messageFail={t('views.navigationsettings-move.form.status.fail')}
        messageForward={t('views.navigationsettings-move.form.status.successForward')}
        customErrorTypeMessages={pageMoveFormCustomErrorTypeMessages}
        active={!!selectedPage && fetchState.type === 'initial'}
        fetchState={fetchState}
      />
    </>
  );
}
